import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Hotel, Plane, Plus, Edit2, Trash2, ExternalLink, Link as LinkIcon, Search, Star, MapPin, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Separator } from '@/components/ui/separator';
import { hotelsFlights, seller } from '@/lib/api';
import { toast } from 'sonner';

const hotelTypes = [
  { value: 'hotel', label: 'Otel' },
  { value: 'hostel', label: 'Hostel' },
  { value: 'apartment', label: 'Apart' },
  { value: 'villa', label: 'Villa' },
  { value: 'resort', label: 'Resort' },
];

const hotelAmenities = [
  'WiFi', 'Havuz', 'Spa', 'Gym', 'Restoran', 'Bar',
  'Otopark', 'Havalimanı Servisi', 'Oda Servisi', 'Klima',
  'TV', 'Mini Bar', 'Kasa', 'Balkon'
];

const integrationTypes = [
  { value: 'booking_com', label: 'Booking.com API', icon: '🔗' },
  { value: 'amadeus', label: 'Amadeus API', icon: '✈️' },
  { value: 'external_link', label: 'Harici Link', icon: '🌐' },
  { value: 'iframe', label: 'Iframe Embed', icon: '📋' },
];

const HotelFlightTab = () => {
  const { t } = useTranslation();
  const [activeSubTab, setActiveSubTab] = useState('hotels');
  const [hotels, setHotels] = useState([]);
  const [flights, setFlights] = useState([]);
  const [hotelReservations, setHotelReservations] = useState([]);
  const [flightReservations, setFlightReservations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddHotel, setShowAddHotel] = useState(false);
  const [showAddFlight, setShowAddFlight] = useState(false);
  const [showSearchIntegration, setShowSearchIntegration] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchType, setSearchType] = useState('booking');
  
  const [newHotel, setNewHotel] = useState({
    name: '',
    description: '',
    hotel_type: 'hotel',
    star_rating: 3,
    images: [''],
    location: '',
    amenities: [],
    room_types: [{ name: 'Standart Oda', price: '', capacity: 2 }],
    check_in_time: '14:00',
    check_out_time: '11:00',
    integration: null,
    contact_phone: '',
    contact_email: '',
    website: ''
  });

  const [newFlight, setNewFlight] = useState({
    airline_name: '',
    description: '',
    flight_type: 'domestic',
    origin_city: '',
    origin_airport_code: '',
    destination_city: '',
    destination_airport_code: '',
    images: [''],
    integration: null,
    contact_phone: '',
    contact_email: '',
    website: ''
  });

  const [searchParams, setSearchParams] = useState({
    location: '',
    check_in: '',
    check_out: '',
    origin: '',
    destination: '',
    departure_date: '',
    guests: 2
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [hotelsRes, flightsRes, hotelResRes, flightResRes] = await Promise.all([
        seller.hotels(),
        seller.flights(),
        seller.hotelReservations().catch(() => ({ data: [] })),
        seller.flightReservations().catch(() => ({ data: [] }))
      ]);
      setHotels(hotelsRes.data || []);
      setFlights(flightsRes.data || []);
      setHotelReservations(hotelResRes.data || []);
      setFlightReservations(flightResRes.data || []);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateReservationStatus = async (type, reservationId, status) => {
    try {
      if (type === 'hotel') {
        await seller.updateHotelReservationStatus(reservationId, status);
      } else {
        await seller.updateFlightReservationStatus(reservationId, status);
      }
      toast.success(t('seller.statusUpdated'));
      fetchData();
    } catch (error) {
      toast.error(t('seller.statusUpdateFailed'));
    }
  };

  const handleAddHotel = async (e) => {
    e.preventDefault();
    try {
      const hotelData = {
        ...newHotel,
        images: newHotel.images.filter(img => img.trim()),
        room_types: newHotel.room_types.map(rt => ({
          ...rt,
          price: parseFloat(rt.price) || 0
        }))
      };
      await hotelsFlights.createHotel(hotelData);
      toast.success(t('seller.hotelCreated'));
      setShowAddHotel(false);
      resetHotelForm();
      fetchData();
    } catch (error) {
      toast.error(t('seller.hotelCreateFailed'));
    }
  };

  const handleAddFlight = async (e) => {
    e.preventDefault();
    try {
      const flightData = {
        ...newFlight,
        images: newFlight.images.filter(img => img.trim())
      };
      await hotelsFlights.createFlight(flightData);
      toast.success(t('seller.flightCreated'));
      setShowAddFlight(false);
      resetFlightForm();
      fetchData();
    } catch (error) {
      toast.error(t('seller.flightCreateFailed'));
    }
  };

  const handleDeleteHotel = async (hotelId) => {
    if (!window.confirm(t('seller.confirmDeleteHotel'))) return;
    try {
      await hotelsFlights.deleteHotel(hotelId);
      toast.success(t('seller.hotelDeleted'));
      fetchData();
    } catch (error) {
      toast.error(t('seller.hotelDeleteFailed'));
    }
  };

  const handleDeleteFlight = async (flightId) => {
    if (!window.confirm(t('seller.confirmDeleteFlight'))) return;
    try {
      await hotelsFlights.deleteFlight(flightId);
      toast.success(t('seller.flightDeleted'));
      fetchData();
    } catch (error) {
      toast.error(t('seller.flightDeleteFailed'));
    }
  };

  const handleSearchIntegration = async () => {
    setSearchLoading(true);
    setSearchResults([]);
    try {
      let response;
      if (searchType === 'booking') {
        response = await hotelsFlights.searchBookingCom({
          location: searchParams.location,
          check_in: searchParams.check_in,
          check_out: searchParams.check_out,
          guests: searchParams.guests
        });
      } else {
        response = await hotelsFlights.searchAmadeus({
          origin: searchParams.origin,
          destination: searchParams.destination,
          departure_date: searchParams.departure_date
        });
      }
      setSearchResults(response.data.results || []);
    } catch (error) {
      toast.error(t('seller.searchFailed'));
    } finally {
      setSearchLoading(false);
    }
  };

  const resetHotelForm = () => {
    setNewHotel({
      name: '',
      description: '',
      hotel_type: 'hotel',
      star_rating: 3,
      images: [''],
      location: '',
      amenities: [],
      room_types: [{ name: 'Standart Oda', price: '', capacity: 2 }],
      check_in_time: '14:00',
      check_out_time: '11:00',
      integration: null,
      contact_phone: '',
      contact_email: '',
      website: ''
    });
  };

  const resetFlightForm = () => {
    setNewFlight({
      airline_name: '',
      description: '',
      flight_type: 'domestic',
      origin_city: '',
      origin_airport_code: '',
      destination_city: '',
      destination_airport_code: '',
      images: [''],
      integration: null,
      contact_phone: '',
      contact_email: '',
      website: ''
    });
  };

  const toggleAmenity = (amenity) => {
    if (newHotel.amenities.includes(amenity)) {
      setNewHotel({ ...newHotel, amenities: newHotel.amenities.filter(a => a !== amenity) });
    } else {
      setNewHotel({ ...newHotel, amenities: [...newHotel.amenities, amenity] });
    }
  };

  const addRoomType = () => {
    setNewHotel({
      ...newHotel,
      room_types: [...newHotel.room_types, { name: '', price: '', capacity: 2 }]
    });
  };

  const updateRoomType = (index, field, value) => {
    const newRoomTypes = [...newHotel.room_types];
    newRoomTypes[index][field] = value;
    setNewHotel({ ...newHotel, room_types: newRoomTypes });
  };

  const removeRoomType = (index) => {
    setNewHotel({
      ...newHotel,
      room_types: newHotel.room_types.filter((_, i) => i !== index)
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <Hotel className="w-6 h-6" />
            <Plane className="w-6 h-6" />
            {t('seller.hotelFlights')}
          </h2>
          <p className="text-muted-foreground">{t('seller.manageHotelFlights')}</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" className="rounded-full" onClick={() => setShowSearchIntegration(true)}>
            <Search className="w-4 h-4 mr-2" />
            {t('seller.searchIntegrations')}
          </Button>
        </div>
      </div>

      {/* Sub-tabs for Hotels, Flights and Reservations */}
      <Tabs value={activeSubTab} onValueChange={setActiveSubTab}>
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="hotels" className="flex items-center gap-2">
            <Hotel className="w-4 h-4" />
            {t('seller.hotels')} ({hotels.length})
          </TabsTrigger>
          <TabsTrigger value="flights" className="flex items-center gap-2">
            <Plane className="w-4 h-4" />
            {t('seller.flights')} ({flights.length})
          </TabsTrigger>
          <TabsTrigger value="hotel-reservations" className="flex items-center gap-2">
            <Hotel className="w-4 h-4" />
            {t('seller.hotelReservations')} ({hotelReservations.length})
          </TabsTrigger>
          <TabsTrigger value="flight-reservations" className="flex items-center gap-2">
            <Plane className="w-4 h-4" />
            {t('seller.flightReservations')} ({flightReservations.length})
          </TabsTrigger>
        </TabsList>

        {/* Hotels Tab */}
        <TabsContent value="hotels" className="space-y-4">
          <div className="flex justify-end">
            <Dialog open={showAddHotel} onOpenChange={setShowAddHotel}>
              <DialogTrigger asChild>
                <Button className="rounded-full">
                  <Plus className="w-4 h-4 mr-2" />
                  {t('seller.addHotel')}
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>{t('seller.addNewHotel')}</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleAddHotel} className="space-y-4">
                  {/* Basic Info */}
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label>{t('seller.hotelName')}</Label>
                      <Input
                        value={newHotel.name}
                        onChange={(e) => setNewHotel({ ...newHotel, name: e.target.value })}
                        required
                      />
                    </div>
                    <div>
                      <Label>{t('seller.hotelType')}</Label>
                      <Select value={newHotel.hotel_type} onValueChange={(v) => setNewHotel({ ...newHotel, hotel_type: v })}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {hotelTypes.map(t => (
                            <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label>{t('seller.starRating')}</Label>
                      <Select value={String(newHotel.star_rating)} onValueChange={(v) => setNewHotel({ ...newHotel, star_rating: parseInt(v) })}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {[1, 2, 3, 4, 5].map(r => (
                            <SelectItem key={r} value={String(r)}>
                              {'⭐'.repeat(r)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>{t('seller.location')}</Label>
                      <Input
                        value={newHotel.location}
                        onChange={(e) => setNewHotel({ ...newHotel, location: e.target.value })}
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <Label>{t('seller.description')}</Label>
                    <Textarea
                      value={newHotel.description}
                      onChange={(e) => setNewHotel({ ...newHotel, description: e.target.value })}
                      required
                    />
                  </div>

                  {/* Amenities */}
                  <div className="border rounded-lg p-4 space-y-3">
                    <Label className="text-base font-medium">🏨 {t('seller.amenities')}</Label>
                    <div className="flex flex-wrap gap-2">
                      {hotelAmenities.map(amenity => (
                        <button
                          key={amenity}
                          type="button"
                          onClick={() => toggleAmenity(amenity)}
                          className={`px-3 py-1 rounded-full text-sm transition-colors ${
                            newHotel.amenities.includes(amenity)
                              ? 'bg-primary text-primary-foreground'
                              : 'bg-secondary hover:bg-secondary/80'
                          }`}
                        >
                          {amenity}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Room Types */}
                  <div className="border rounded-lg p-4 space-y-3">
                    <div className="flex items-center justify-between">
                      <Label className="text-base font-medium">🛏️ {t('seller.roomTypes')}</Label>
                      <Button type="button" variant="outline" size="sm" onClick={addRoomType}>
                        <Plus className="w-4 h-4 mr-1" /> {t('seller.addRoom')}
                      </Button>
                    </div>
                    {newHotel.room_types.map((room, index) => (
                      <div key={index} className="grid grid-cols-4 gap-2 items-end">
                        <div>
                          <Label className="text-xs">{t('seller.roomName')}</Label>
                          <Input
                            value={room.name}
                            onChange={(e) => updateRoomType(index, 'name', e.target.value)}
                            placeholder="Standart Oda"
                          />
                        </div>
                        <div>
                          <Label className="text-xs">{t('seller.pricePerNight')}</Label>
                          <Input
                            type="number"
                            value={room.price}
                            onChange={(e) => updateRoomType(index, 'price', e.target.value)}
                            placeholder="100"
                          />
                        </div>
                        <div>
                          <Label className="text-xs">{t('seller.capacity')}</Label>
                          <Input
                            type="number"
                            min="1"
                            max="10"
                            value={room.capacity}
                            onChange={(e) => updateRoomType(index, 'capacity', parseInt(e.target.value))}
                          />
                        </div>
                        {newHotel.room_types.length > 1 && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="text-destructive"
                            onClick={() => removeRoomType(index)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    ))}
                  </div>

                  {/* Integration */}
                  <div className="border rounded-lg p-4 space-y-3">
                    <Label className="text-base font-medium">🔗 {t('seller.integration')}</Label>
                    <Select
                      value={newHotel.integration?.type || 'none'}
                      onValueChange={(v) => setNewHotel({
                        ...newHotel,
                        integration: v === 'none' ? null : { type: v, external_url: '', is_active: true }
                      })}
                    >
                      <SelectTrigger><SelectValue placeholder={t('seller.selectIntegration')} /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">{t('seller.noIntegration')}</SelectItem>
                        {integrationTypes.map(i => (
                          <SelectItem key={i.value} value={i.value}>
                            {i.icon} {i.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {newHotel.integration && ['external_link', 'iframe'].includes(newHotel.integration.type) && (
                      <Input
                        value={newHotel.integration.external_url || ''}
                        onChange={(e) => setNewHotel({
                          ...newHotel,
                          integration: { ...newHotel.integration, external_url: e.target.value }
                        })}
                        placeholder="https://..."
                      />
                    )}
                  </div>

                  {/* Contact & Image */}
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label>{t('seller.contactPhone')}</Label>
                      <Input
                        value={newHotel.contact_phone}
                        onChange={(e) => setNewHotel({ ...newHotel, contact_phone: e.target.value })}
                      />
                    </div>
                    <div>
                      <Label>{t('seller.contactEmail')}</Label>
                      <Input
                        type="email"
                        value={newHotel.contact_email}
                        onChange={(e) => setNewHotel({ ...newHotel, contact_email: e.target.value })}
                      />
                    </div>
                  </div>

                  <div>
                    <Label>{t('seller.imageUrl')}</Label>
                    <Input
                      value={newHotel.images[0]}
                      onChange={(e) => setNewHotel({ ...newHotel, images: [e.target.value] })}
                    />
                  </div>

                  <Button type="submit" className="w-full rounded-full">
                    {t('seller.createHotel')}
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          {/* Hotels Table */}
          <Card>
            <CardContent className="pt-6">
              {hotels.length === 0 ? (
                <div className="text-center py-8">
                  <Hotel className="w-12 h-12 mx-auto text-muted-foreground/30 mb-3" />
                  <p className="text-muted-foreground">{t('seller.noHotels')}</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t('seller.hotel')}</TableHead>
                      <TableHead>{t('seller.type')}</TableHead>
                      <TableHead>{t('seller.location')}</TableHead>
                      <TableHead>{t('seller.integration')}</TableHead>
                      <TableHead className="text-right">{t('seller.actions')}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {hotels.map((hotel) => (
                      <TableRow key={hotel.id}>
                        <TableCell>
                          <div className="flex items-center gap-3">
                            <div className="w-16 h-12 rounded-lg overflow-hidden bg-secondary">
                              <img
                                src={hotel.images?.[0] || '/placeholder-hotel.jpg'}
                                alt={hotel.name}
                                className="w-full h-full object-cover"
                              />
                            </div>
                            <div>
                              <p className="font-medium">{hotel.name}</p>
                              <p className="text-xs">{'⭐'.repeat(hotel.star_rating)}</p>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="secondary">
                            {hotelTypes.find(t => t.value === hotel.hotel_type)?.label || hotel.hotel_type}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1 text-sm">
                            <MapPin className="w-3 h-3" />
                            {hotel.location}
                          </div>
                        </TableCell>
                        <TableCell>
                          {hotel.integration ? (
                            <Badge variant="outline">
                              {integrationTypes.find(i => i.value === hotel.integration.type)?.label || hotel.integration.type}
                            </Badge>
                          ) : (
                            <span className="text-muted-foreground text-sm">-</span>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-2">
                            {hotel.integration?.external_url && (
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => window.open(hotel.integration.external_url, '_blank')}
                              >
                                <ExternalLink className="w-4 h-4" />
                              </Button>
                            )}
                            <Button
                              variant="ghost"
                              size="icon"
                              className="text-destructive"
                              onClick={() => handleDeleteHotel(hotel.id)}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Flights Tab */}
        <TabsContent value="flights" className="space-y-4">
          <div className="flex justify-end">
            <Dialog open={showAddFlight} onOpenChange={setShowAddFlight}>
              <DialogTrigger asChild>
                <Button className="rounded-full">
                  <Plus className="w-4 h-4 mr-2" />
                  {t('seller.addFlight')}
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>{t('seller.addNewFlight')}</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleAddFlight} className="space-y-4">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label>{t('seller.airlineName')}</Label>
                      <Input
                        value={newFlight.airline_name}
                        onChange={(e) => setNewFlight({ ...newFlight, airline_name: e.target.value })}
                        required
                      />
                    </div>
                    <div>
                      <Label>{t('seller.flightType')}</Label>
                      <Select value={newFlight.flight_type} onValueChange={(v) => setNewFlight({ ...newFlight, flight_type: v })}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="domestic">İç Hat</SelectItem>
                          <SelectItem value="international">Dış Hat</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div>
                    <Label>{t('seller.description')}</Label>
                    <Textarea
                      value={newFlight.description}
                      onChange={(e) => setNewFlight({ ...newFlight, description: e.target.value })}
                    />
                  </div>

                  <Separator />
                  <p className="text-sm font-medium">{t('seller.routeInfo')}</p>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label>{t('seller.originCity')}</Label>
                      <Input
                        value={newFlight.origin_city}
                        onChange={(e) => setNewFlight({ ...newFlight, origin_city: e.target.value })}
                        required
                      />
                    </div>
                    <div>
                      <Label>{t('seller.originAirportCode')}</Label>
                      <Input
                        value={newFlight.origin_airport_code}
                        onChange={(e) => setNewFlight({ ...newFlight, origin_airport_code: e.target.value.toUpperCase() })}
                        placeholder="IST"
                        maxLength={3}
                        required
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label>{t('seller.destinationCity')}</Label>
                      <Input
                        value={newFlight.destination_city}
                        onChange={(e) => setNewFlight({ ...newFlight, destination_city: e.target.value })}
                        required
                      />
                    </div>
                    <div>
                      <Label>{t('seller.destinationAirportCode')}</Label>
                      <Input
                        value={newFlight.destination_airport_code}
                        onChange={(e) => setNewFlight({ ...newFlight, destination_airport_code: e.target.value.toUpperCase() })}
                        placeholder="AYT"
                        maxLength={3}
                        required
                      />
                    </div>
                  </div>

                  {/* Integration */}
                  <div className="border rounded-lg p-4 space-y-3">
                    <Label className="text-base font-medium">🔗 {t('seller.integration')}</Label>
                    <Select
                      value={newFlight.integration?.type || 'none'}
                      onValueChange={(v) => setNewFlight({
                        ...newFlight,
                        integration: v === 'none' ? null : { type: v, external_url: '', is_active: true }
                      })}
                    >
                      <SelectTrigger><SelectValue placeholder={t('seller.selectIntegration')} /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">{t('seller.noIntegration')}</SelectItem>
                        {integrationTypes.map(i => (
                          <SelectItem key={i.value} value={i.value}>
                            {i.icon} {i.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {newFlight.integration && ['external_link', 'iframe'].includes(newFlight.integration.type) && (
                      <Input
                        value={newFlight.integration.external_url || ''}
                        onChange={(e) => setNewFlight({
                          ...newFlight,
                          integration: { ...newFlight.integration, external_url: e.target.value }
                        })}
                        placeholder="https://..."
                      />
                    )}
                  </div>

                  <Button type="submit" className="w-full rounded-full">
                    {t('seller.createFlight')}
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          {/* Flights Table */}
          <Card>
            <CardContent className="pt-6">
              {flights.length === 0 ? (
                <div className="text-center py-8">
                  <Plane className="w-12 h-12 mx-auto text-muted-foreground/30 mb-3" />
                  <p className="text-muted-foreground">{t('seller.noFlights')}</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t('seller.airline')}</TableHead>
                      <TableHead>{t('seller.route')}</TableHead>
                      <TableHead>{t('seller.type')}</TableHead>
                      <TableHead>{t('seller.integration')}</TableHead>
                      <TableHead className="text-right">{t('seller.actions')}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {flights.map((flight) => (
                      <TableRow key={flight.id}>
                        <TableCell>
                          <p className="font-medium">{flight.airline_name}</p>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <span className="font-mono text-sm">{flight.origin_airport_code}</span>
                            <Plane className="w-4 h-4 text-muted-foreground" />
                            <span className="font-mono text-sm">{flight.destination_airport_code}</span>
                          </div>
                          <p className="text-xs text-muted-foreground">
                            {flight.origin_city} → {flight.destination_city}
                          </p>
                        </TableCell>
                        <TableCell>
                          <Badge variant="secondary">
                            {flight.flight_type === 'domestic' ? 'İç Hat' : 'Dış Hat'}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {flight.integration ? (
                            <Badge variant="outline">
                              {integrationTypes.find(i => i.value === flight.integration.type)?.label || flight.integration.type}
                            </Badge>
                          ) : (
                            <span className="text-muted-foreground text-sm">-</span>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-2">
                            {flight.integration?.external_url && (
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => window.open(flight.integration.external_url, '_blank')}
                              >
                                <ExternalLink className="w-4 h-4" />
                              </Button>
                            )}
                            <Button
                              variant="ghost"
                              size="icon"
                              className="text-destructive"
                              onClick={() => handleDeleteFlight(flight.id)}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Hotel Reservations Tab */}
        <TabsContent value="hotel-reservations" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Hotel className="w-5 h-5" />
                {t('seller.hotelReservations')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {hotelReservations.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">{t('seller.noReservations')}</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t('seller.customer')}</TableHead>
                      <TableHead>{t('seller.dates')}</TableHead>
                      <TableHead>{t('seller.roomType')}</TableHead>
                      <TableHead>{t('seller.guests')}</TableHead>
                      <TableHead>{t('seller.totalPrice')}</TableHead>
                      <TableHead>{t('seller.status')}</TableHead>
                      <TableHead>{t('common.actions')}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {hotelReservations.map((res) => (
                      <TableRow key={res.id}>
                        <TableCell>
                          <div>
                            <p className="font-medium">{res.customer_name}</p>
                            <p className="text-xs text-muted-foreground">{res.customer_email}</p>
                            <p className="text-xs text-muted-foreground">{res.customer_phone}</p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="text-sm">
                            <p>{res.check_in_date}</p>
                            <p className="text-muted-foreground">→ {res.check_out_date}</p>
                          </div>
                        </TableCell>
                        <TableCell>{res.room_type}</TableCell>
                        <TableCell>{res.guests}</TableCell>
                        <TableCell className="font-medium">${res.total_price?.toFixed(2)}</TableCell>
                        <TableCell>
                          <Badge variant={
                            res.status === 'confirmed' ? 'default' :
                            res.status === 'cancelled' ? 'destructive' :
                            res.status === 'completed' ? 'secondary' : 'outline'
                          }>
                            {res.status}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Select
                            value={res.status}
                            onValueChange={(v) => updateReservationStatus('hotel', res.id, v)}
                          >
                            <SelectTrigger className="w-32">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="pending">{t('seller.pending')}</SelectItem>
                              <SelectItem value="confirmed">{t('seller.confirmed')}</SelectItem>
                              <SelectItem value="cancelled">{t('seller.cancelled')}</SelectItem>
                              <SelectItem value="completed">{t('seller.completed')}</SelectItem>
                            </SelectContent>
                          </Select>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Flight Reservations Tab */}
        <TabsContent value="flight-reservations" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Plane className="w-5 h-5" />
                {t('seller.flightReservations')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {flightReservations.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">{t('seller.noReservations')}</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t('seller.customer')}</TableHead>
                      <TableHead>{t('seller.departure')}</TableHead>
                      <TableHead>{t('seller.passengers')}</TableHead>
                      <TableHead>{t('seller.class')}</TableHead>
                      <TableHead>{t('seller.totalPrice')}</TableHead>
                      <TableHead>{t('seller.status')}</TableHead>
                      <TableHead>{t('common.actions')}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {flightReservations.map((res) => (
                      <TableRow key={res.id}>
                        <TableCell>
                          <div>
                            <p className="font-medium">{res.customer_name}</p>
                            <p className="text-xs text-muted-foreground">{res.customer_email}</p>
                            <p className="text-xs text-muted-foreground">{res.customer_phone}</p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="text-sm">
                            <p>{res.departure_date}</p>
                            {res.return_date && <p className="text-muted-foreground">↔ {res.return_date}</p>}
                          </div>
                        </TableCell>
                        <TableCell>{res.passengers}</TableCell>
                        <TableCell>
                          <Badge variant="outline">{res.class_type}</Badge>
                        </TableCell>
                        <TableCell className="font-medium">${res.total_price?.toFixed(2)}</TableCell>
                        <TableCell>
                          <Badge variant={
                            res.status === 'confirmed' ? 'default' :
                            res.status === 'cancelled' ? 'destructive' :
                            res.status === 'completed' ? 'secondary' : 'outline'
                          }>
                            {res.status}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Select
                            value={res.status}
                            onValueChange={(v) => updateReservationStatus('flight', res.id, v)}
                          >
                            <SelectTrigger className="w-32">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="pending">{t('seller.pending')}</SelectItem>
                              <SelectItem value="confirmed">{t('seller.confirmed')}</SelectItem>
                              <SelectItem value="cancelled">{t('seller.cancelled')}</SelectItem>
                              <SelectItem value="completed">{t('seller.completed')}</SelectItem>
                            </SelectContent>
                          </Select>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Integration Search Dialog */}
      <Dialog open={showSearchIntegration} onOpenChange={setShowSearchIntegration}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>🔍 {t('seller.searchIntegrations')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <Tabs value={searchType} onValueChange={setSearchType}>
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="booking">🏨 Booking.com</TabsTrigger>
                <TabsTrigger value="amadeus">✈️ Amadeus</TabsTrigger>
              </TabsList>

              <TabsContent value="booking" className="space-y-4">
                <div className="grid md:grid-cols-3 gap-4">
                  <div>
                    <Label>{t('seller.location')}</Label>
                    <Input
                      value={searchParams.location}
                      onChange={(e) => setSearchParams({ ...searchParams, location: e.target.value })}
                      placeholder="İstanbul"
                    />
                  </div>
                  <div>
                    <Label>{t('seller.checkIn')}</Label>
                    <Input
                      type="date"
                      value={searchParams.check_in}
                      onChange={(e) => setSearchParams({ ...searchParams, check_in: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label>{t('seller.checkOut')}</Label>
                    <Input
                      type="date"
                      value={searchParams.check_out}
                      onChange={(e) => setSearchParams({ ...searchParams, check_out: e.target.value })}
                    />
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="amadeus" className="space-y-4">
                <div className="grid md:grid-cols-3 gap-4">
                  <div>
                    <Label>{t('seller.origin')}</Label>
                    <Input
                      value={searchParams.origin}
                      onChange={(e) => setSearchParams({ ...searchParams, origin: e.target.value })}
                      placeholder="IST"
                    />
                  </div>
                  <div>
                    <Label>{t('seller.destination')}</Label>
                    <Input
                      value={searchParams.destination}
                      onChange={(e) => setSearchParams({ ...searchParams, destination: e.target.value })}
                      placeholder="AYT"
                    />
                  </div>
                  <div>
                    <Label>{t('seller.departureDate')}</Label>
                    <Input
                      type="date"
                      value={searchParams.departure_date}
                      onChange={(e) => setSearchParams({ ...searchParams, departure_date: e.target.value })}
                    />
                  </div>
                </div>
              </TabsContent>
            </Tabs>

            <Button onClick={handleSearchIntegration} disabled={searchLoading} className="w-full">
              {searchLoading ? 'Aranıyor...' : t('seller.search')}
            </Button>

            {/* Search Results */}
            {searchResults.length > 0 && (
              <div className="space-y-3">
                <Separator />
                <p className="font-medium">{t('seller.searchResults')} ({searchResults.length})</p>
                <p className="text-xs text-muted-foreground bg-yellow-50 p-2 rounded">⚠️ {t('seller.mockDataNote')}</p>
                <div className="space-y-3">
                  {searchResults.map((result, index) => (
                    <Card key={index} className="overflow-hidden">
                      <CardContent className="p-4">
                        <div className="flex items-center gap-4">
                          {result.images?.[0] && (
                            <img src={result.images[0]} alt="" className="w-20 h-16 rounded object-cover" />
                          )}
                          <div className="flex-1">
                            <p className="font-medium">{result.name || result.airline}</p>
                            {result.star_rating && (
                              <p className="text-xs">{'⭐'.repeat(result.star_rating)}</p>
                            )}
                            {result.flight_number && (
                              <p className="text-sm text-muted-foreground">
                                {result.origin} → {result.destination} • {result.duration}
                              </p>
                            )}
                            <p className="text-sm text-muted-foreground">{result.location}</p>
                          </div>
                          <div className="text-right">
                            <p className="text-lg font-bold text-primary">
                              ${result.price_per_night || result.price}
                            </p>
                            {result.booking_url && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => window.open(result.booking_url, '_blank')}
                              >
                                <ExternalLink className="w-3 h-3 mr-1" />
                                {t('seller.book')}
                              </Button>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default HotelFlightTab;
